import Label from 'components/form/Label/Label';
import { Link } from 'components/navigation/Link/Link';
import { Tag } from 'components/Tag';
import {
  addDays,
  addMonth,
  formatAsMonthDayOnly,
  getDateWithoutTime,
  getDayOfMonth,
  getDiffDaysBetweenDates,
  getLastDayOfThisMonth,
  getMoment,
  isDateAfterTarget,
  setDate,
} from 'helpers/dateHelper';
import { formatCurrency, formatUSDwithCents } from 'helpers/money.helper';

export const STATE___NO_PAYMENT_MANAGEMENT = 'STATE___NO_PAYMENT_MANAGEMENT';
export const STATE___NO_TRANSACTIONS = 'STATE___NO_TRANSACTIONS';
export const STATE___BALANCE_BUILDING = 'STATE___BALANCE_BUILDING';
export const STATE___PENDING_AUTOMATIC_PAYMENT =
  'STATE___PENDING_AUTOMATIC_PAYMENT';
export const STATE___MANUAL_DUE = 'STATE___MANUAL_DUE';
export const STATE___DELIQUENT = 'STATE___DELIQUENT';
export const STATE___COMPLETED_CYCLE = 'STATE___COMPLETED_CYCLE';
export const STATE___NO_BALANCE_INCURRED = 'STATE___NO_BALANCE_INCURRED';
export const STATE___ZERO_UTILIZATION = 'STATE___ZERO_UTILIZATION';

const menuItemsPartial = [
  {
    text: 'View Statements',
    linkTo: '/cash/builder/statements',
  },
  {
    text: 'Withdraw Deposit',
    linkTo: '/cash/builder/depositWithdrawal',
  },
];
const menuItemsFull = [
  {
    text: 'View Statements',
    linkTo: '/cash/builder/statements',
  },
  {
    text: 'Usage and Payment Settings',
    linkTo: '/cash/builder/utilizationManagement',
  },
  {
    text: 'Withdraw Vault',
    linkTo: '/cash/builder/vaultWithdrawal',
  },
  {
    text: 'Withdraw Deposit',
    linkTo: '/cash/builder/depositWithdrawal',
  },
];

const getSliderColor = percent => {
  if (percent <= 10) {
    return 'green';
  } else if (percent > 10 && percent <= 30) {
    return 'yellow';
  } else if (percent > 30) {
    return 'red';
  }
};
const stateMapping = {
  [STATE___NO_PAYMENT_MANAGEMENT]: (c, state) => {
    return {
      hideBanner: true,
      hidePaymentHeaderCard: true,
      menuItems: menuItemsPartial,
      showActivityCardOnCashScreen: true,
    };
  },
  [STATE___NO_TRANSACTIONS]: (c, state) => {
    const utilfromCard = state?.securedCardSettings?.maxUtilizationAmount;
    const finalMaxUtilizationAmount =
      utilfromCard > 0 ? formatCurrency(utilfromCard) : '$0.00';
    return {
      hideBanner: false,
      menuItems: menuItemsFull,
      bannerType: 'MAKE_PURCHASES',
      bannerTitle: (
        <Label type="Subtitle-1 mSubtitle-2">
          <Tag
            backgroundClass="bgYellow025"
            colorClass="fontYellow400"
            className="margR8"
          >
            MAKE PURCHASES
          </Tag>{' '}
          Start Building Credit
        </Label>
      ),
      bannerOpenByDefault: true,
      bannerMessage: (
        <Label type="Body-2 mCaption">
          Make a debit purchase below your {finalMaxUtilizationAmount} limit,
          and we'll use it to start building your credit!
        </Label>
      ),
      activityBarTitle: `Statement Amount Paid`,
      activityAutoPay: state.isAutomaticPayment ? 'Autopay On' : 'Autopay Off',
      creditUtilization: state?.debitCard?.creditUtilization,
      creditUtilizationSliderColor: getSliderColor(
        state?.creditUtilizationPercent,
      ),
      statementBalanceRemaining: Math.abs(
        state?.previousStatement?.statementBalance,
      ),
      totalCreditUtilized: Math.abs(state?.currentStatement?.statementBalance),
    };
  },
  [STATE___BALANCE_BUILDING]: (c, state) => {
    return {
      hideBanner: false,
      menuItems: menuItemsFull,
      bannerType: 'MAKE_PURCHASES',
      bannerTitle: (
        <Label type="Subtitle-1 mSubtitle-2">
          <Tag backgroundClass="bgGreen025" colorClass="fontGreen400">
            ON-TRACK
          </Tag>{' '}
          Debit purchases found
        </Label>
      ),
      bannerOpenByDefault: false,
      bannerMessage: (
        <Label type="Body-2 mCaption">
          Great job! We found debit purchases to report on your Builder account.
        </Label>
      ),
      activityBarTitle: `Statement Amount Paid`,
      activityAutoPay: state.isAutomaticPayment ? 'Autopay On' : 'Autopay Off',
      creditUtilization: state?.debitCard?.creditUtilization,
      creditUtilizationSliderColor: getSliderColor(
        state?.creditUtilizationPercent,
      ),
      statementBalanceRemaining: Math.abs(
        state?.previousStatement?.statementBalance,
      ),
      totalCreditUtilized: Math.abs(state?.currentStatement?.statementBalance),
    };
  },
  [STATE___PENDING_AUTOMATIC_PAYMENT]: (c, state) => {
    return {
      hideBanner: false,
      bannerTitle: (
        <Label type="Subtitle-1 mSubtitle-2">
          <Tag backgroundClass="bgGreen025" colorClass="fontGreen400">
            ON-TRACK
          </Tag>{' '}
          Auto-payment pending
        </Label>
      ),
      bannerOpenByDefault: false,
      bannerMessage: (
        <Label type="Body-2 mCaption">
          We'll automatically pay your statement balance from the money we've
          set aside - at no cost to you!
        </Label>
      ),
      menuItems: menuItemsFull,
      activityBarTitle: `Statement Amount Due`,
      activityAutoPay: state.isAutomaticPayment ? 'Autopay On' : 'Autopay Off',
      activityBarLink: '/cash/builder/payment',
      creditUtilization: state?.previousStatement?.remainingStatementBalance,
      creditUtilizationSliderColor: getSliderColor(
        state?.creditUtilizationPercent,
      ),
      statementBalanceRemaining: Math.abs(
        state?.previousStatement?.remainingStatementBalance,
      ),
      totalCreditUtilized: Math.abs(state?.debitCard?.balance),
    };
  },
  [STATE___MANUAL_DUE]: (c, state) => {
    return {
      hideBanner: false,
      menuItems: menuItemsFull,
      bannerType: 'MAKE_PAYMENT',
      bannerTitle: (
        <Label type="Subtitle-1 mSubtitle-2">
          <Tag backgroundClass="bgRed025" colorClass="fontRed400">
            MAKE PAYMENT
          </Tag>{' '}
          Manual Payment Due
        </Label>
      ),
      bannerOpenByDefault: true,
      bannerMessage: (
        <Label type="Body-2 mCaption">
          Your payment is due in {state.daysUntilPaymentDue} days. We’ve already
          set aside money to pay your balance!{' '}
          <Link className="bold link-style no-line">Approve Now</Link>
        </Label>
      ),

      bannerIcon: 'builderIconManualPaymentDue',
      bannerLink: '/cash/builder/payment',
      activityBarTitle: `Statement Amount Due`,
      activityAutoPay: `Due in ${state?.daysUntilPaymentDue} day${
        state?.daysUntilPaymentDue === 1 ? '' : 's'
      }`,
      activityBarLink: '/cash/builder/payment',
      creditUtilization: state?.previousStatement?.remainingStatementBalance,
      creditUtilizationSliderColor: getSliderColor(
        state?.creditUtilizationPercent,
      ),
      statementBalanceRemaining: Math.abs(
        state?.previousStatement?.remainingStatementBalance,
      ),
      totalCreditUtilized: Math.abs(state?.debitCard?.balance),
    };
  },
  [STATE___DELIQUENT]: (c, state) => {
    const delinquentAmount = state?.debitCard?.delinquency?.dueAmount;
    state.delinquencyDueAmount = state?.debitCard?.delinquency?.dueAmount;
    const totalDays = Math.abs(state?.debitCard?.delinquency?.totalDays);
    const pluralS = totalDays !== 1 ? 's' : '';
    const warningText =
      totalDays === 0 ? 'now overdue' : `${totalDays} day${pluralS} overdue`;
    const warningTextHeader =
      totalDays === 0 ? 'Now Overdue' : `${totalDays} Day${pluralS} Overdue`;
    return {
      hideBanner: false,
      menuItems: menuItemsFull,
      bannerType: 'MAKE_OVERDUE_PAYMENT',
      bannerTitle: (
        <Label type="Subtitle-1 mSubtitle-2 fontRed300">
          <Tag backgroundClass="bgRed025" colorClass="fontRed400">
            OVERDUE
          </Tag>{' '}
          Balance {warningTextHeader}
        </Label>
      ),
      bannerOpenByDefault: true,
      bannerMessage: (
        <Label type="Body-2 mCaption">
          Your payment of {formatUSDwithCents(delinquentAmount)} is{' '}
          <span className="">{warningText}</span>.{' '}
          <Link className="bold link-style no-line">Pay now</Link> to avoid
          further impact to your credit.
        </Label>
      ),

      bannerIcon: 'builderDelinquentPayment',
      bannerLink: '/cash/builder/payment',
      activityBarTitle: <span className="">Amount Overdue</span>,
      activityAutoPay: (
        <span className="Body-2 fontRed300">{warningTextHeader}</span>
      ),
      activityBarLink: '/cash/builder/payment',
      creditUtilization: state?.debitCard?.delinquency?.dueAmount,
      creditUtilizationSliderColor: getSliderColor(
        state?.creditUtilizationPercent,
      ),
      statementBalanceRemaining: Math.abs(
        state?.previousStatement?.remainingStatementBalance,
      ),
      totalCreditUtilized: Math.abs(state?.debitCard?.balance),
    };
  },
  [STATE___COMPLETED_CYCLE]: (c, state) => {
    return {
      hideBanner: false,
      menuItems: menuItemsFull,
      bannerType: 'REVIEW_YOUR_ACTIVITY',

      bannerTitle: (
        <Label type="Subtitle-1 mSubtitle-2 ">
          <Tag backgroundClass="bgGreen025" colorClass="fontGreen400">
            ON-TRACK
          </Tag>{' '}
          See how you built your credit
        </Label>
      ),
      bannerOpenByDefault: false,
      bannerMessage: (
        <Label type="Body-2 mCaption">
          Nice work! See the steps you took this month to help grow your credit
          with debit.
        </Label>
      ),
      hideBannerOnBuilderHome: true,
      bannerIcon: 'builderIconCompletedCycle',
      bannerLink: null,
      activityBarTitle: `Statement Amount Paid`,
      activityStatementDue: 0.0,
      activityAutoPay: state.isAutomaticPayment ? 'Autopay On' : 'Autopay Off',
      creditUtilization: state?.debitCard?.creditUtilization,
      creditUtilizationSliderColor: getSliderColor(
        state?.creditUtilizationPercent,
      ),
      statementBalanceRemaining: Math.abs(
        state?.previousStatement?.statementBalance,
      ),
      totalCreditUtilized: Math.abs(state?.previousStatement?.statementBalance),
    };
  },
  [STATE___NO_BALANCE_INCURRED]: (c, state) => {
    return {
      hideBanner: false,
      menuItems: menuItemsFull,
      bannerType: 'UNABLE_TO_FIND_TRANSACTIONS',
      bannerTitle: (
        <Label type="Subtitle-1 mSubtitle-2fontRed300">
          <Tag backgroundClass="bgOrange025" colorClass="fontOrange400">
            ATTENTION
          </Tag>{' '}
          No eligible transactions
        </Label>
      ),
      bannerOpenByDefault: true,
      bannerMessage: (
        <Label type="Body-2 mCaption">
          We couldn't find any debit purchases below $
          {state?.securedCardSettings?.maxUtilizationAmount} last month. Use
          your Sesame Cash card to start building your credit!
        </Label>
      ),

      bannerIcon: 'builderIconNoBalanceIncurred',
      bannerLink: null,
      activityBarTitle: 'Statement Amount Due',
      activityAutoPay: state.isAutomaticPayment ? 'Autopay On' : 'Autopay Off',
      activityBarLink: null,
      activityStatementDue: 0.0,
      creditUtilization: state?.debitCard?.creditUtilization,
      creditUtilizationSliderColor: getSliderColor(
        state?.creditUtilizationPercent,
      ),
      statementBalanceRemaining: Math.abs(
        state?.previousStatement?.remainingStatementBalance,
      ),
      totalCreditUtilized: Math.abs(state?.currentStatement?.statementBalance),
    };
  },
  [STATE___ZERO_UTILIZATION]: (c, state) => {
    return {
      hideBanner: false,
      menuItems: menuItemsFull,
      bannerType: 'UNABLE_TO_FIND_TRANSACTIONS',
      bannerTitle: (
        <Label type="Subtitle-1 mSubtitle-2">
          Update your utilization limit
        </Label>
      ),
      bannerOpenByDefault: true,
      bannerMessage: (
        <Label type="Body-2 mCaption">
          Your utilization limit is currently $0.00.{' '}
          <Link className="bold link-style no-line">Increase your limit</Link>{' '}
          and we'll start using your everyday debit purchases to build your
          credit.
        </Label>
      ),

      bannerIcon: 'builderIconNoBalanceIncurred',
      bannerLink: '/cash/builder/utilizationManagement',
      activityBarTitle: `Statement Amount Due`,
      activityAutoPay: `Due in ${state?.daysUntilPaymentDue} day${
        state?.daysUntilPaymentDue === 1 ? '' : 's'
      }`,
      activityStatementDue: state?.previousStatement?.remainingStatementBalance,
      activityBarLink: '/cash/builder/payment',
      creditUtilization: state?.debitCard?.creditUtilization,
      creditUtilizationSliderColor: getSliderColor(
        state?.creditUtilizationPercent,
      ),
      statementBalanceRemaining: Math.abs(
        state?.previousStatement?.remainingStatementBalance,
      ),
      totalCreditUtilized: Math.abs(state?.currentStatement?.statementBalance),
    };
  },
};

const getHighLevelState = (c, state) => {
  const { previousStatement, debitCard } = state;
  const paymentStatus = previousStatement?.paymentStatus;
  const currentStatemenPaymentStatus = state?.previousStatement?.paymentStatus;
  state.paymentStatus = paymentStatus;

  const isPaymentCompleteOrNone =
    currentStatemenPaymentStatus === 'COMPLETED' ||
    currentStatemenPaymentStatus === 'NONE';
  const isPaymentPending =
    state?.previousStatement?.paymentStatus === 'PENDING';
  const isPaymentFailed = paymentStatus === 'FAILED';

  const isThereAnyDelinquentAmount = debitCard?.delinquency?.dueAmount > 0;
  const dayOfMonth = getDayOfMonth(state?.refTime);
  const inMonth_Post_statement = dayOfMonth >= 6; // before 6th
  const newBuilderUser = c?.recentSecuredCardStatements?.length <= 1; // statements only return 1
  const isAutomaticPayment =
    c?.securedCardSettings?.utilizationMethod === 'AUTOMATIC';
  state.isAutomaticPayment = isAutomaticPayment;
  const utilizationStatus = previousStatement?.utilizationStatus;
  const noUtilizationAmountIncurred =
    utilizationStatus === 'NONE' || utilizationStatus === 'FAILED';
  const isPaymentComplete = utilizationStatus === 'COMPLETED';

  state.highLevelStateDebug = {
    refTime: state?.refTime,
    paymentStatus,

    isPaymentCompleteOrNone,
    isPaymentComplete,
    isPaymentPending,
    isPaymentFailed,
    isThereAnyDelinquentAmount,
    dayOfMonth,
    inMonth_Post_statement,
    newBuilderUser,
    isAutomaticPayment,
    utilizationStatus,
    noUtilizationAmountIncurred,
  };

  if (!state?.hasSelectedUtilizationAndPaymentManagement) {
    state.highLevelState = STATE___NO_PAYMENT_MANAGEMENT;
  } else if (state?.securedCard?.maxUtilizationAmount === 0) {
    state.highLevelState = STATE___ZERO_UTILIZATION;
  } else if (
    (isPaymentCompleteOrNone && inMonth_Post_statement) ||
    newBuilderUser
  ) {
    if (state?.currentStatement?.statementBalance > 0) {
      state.highLevelState = STATE___BALANCE_BUILDING;
    } else {
      state.highLevelState = STATE___NO_TRANSACTIONS;
    }
  } else if (
    isPaymentPending &&
    !isThereAnyDelinquentAmount &&
    isAutomaticPayment
  ) {
    state.highLevelState = STATE___PENDING_AUTOMATIC_PAYMENT;
  } else if (isThereAnyDelinquentAmount) {
    state.highLevelState = STATE___DELIQUENT;
  } else if (
    ((isPaymentPending && !isAutomaticPayment) || isPaymentFailed) &&
    !isThereAnyDelinquentAmount
  ) {
    state.highLevelState = STATE___MANUAL_DUE;
  } else if (isPaymentComplete && !inMonth_Post_statement) {
    state.highLevelState = STATE___COMPLETED_CYCLE;
  } else if (
    noUtilizationAmountIncurred &&
    !isThereAnyDelinquentAmount &&
    !inMonth_Post_statement
  ) {
    state.highLevelState = STATE___NO_BALANCE_INCURRED;
  }

  return state.highLevelState;
};

const checkAndAddLookingForSesameCashPurchases = (
  c,
  state,
  addMonthAmount = -1,
  targetStatementDate,
) => {
  const targetDate = addDays(
    getLastDayOfThisMonth(
      addMonth(targetStatementDate ?? state?.refTime, addMonthAmount),
    ),
    -1,
  );
  state?.activitySummaryList.push({
    label: formatAsMonthDayOnly(targetDate),
    step: true,
    content:
      'Looking for Sesame Cash debit purchases that fit your usage limit',
    active:
      isDateAfterTarget(getMoment(state?.refTime), targetDate) ||
      formatAsMonthDayOnly(state?.refTime) === formatAsMonthDayOnly(targetDate),
  });
};

const checkAndAddAttemptToFindPurchases = (
  c,
  state,
  addMonthAmount,
  targetStatementArg,
) => {
  let targetStatement = state?.previousStatement;
  if (targetStatementArg) {
    targetStatement = targetStatementArg;
  }
  if (
    targetStatement.utilizationStatus === 'PENDING' ||
    targetStatement.utilizationStatus === 'COMPLETED' ||
    targetStatement.utilizationStatus === 'SUCEEDED' ||
    targetStatement.utilizationStatus === 'NONE'
  ) {
    let message = `We've tallied debit purchases to create a balance on your secured account and set aside money to pay it off`;
    const targetDate = getLastDayOfThisMonth(
      addMonth(targetStatement?.statementDate, addMonthAmount),
    );
    state?.activitySummaryList.push({
      label: formatAsMonthDayOnly(targetDate),
      content: message,
      active:
        targetStatement?.utilizationStatus === 'COMPLETED' ||
        targetStatement.utilizationStatus === 'NONE',
    });
  } else if (targetStatement?.utilizationStatus === 'FAILED') {
    let message = `We couldn't find any debit purchases below $${state?.securedCardSettings?.maxUtilizationAmount} this month. Use Sesame Cash to build credit! `;
    const targetDate = getLastDayOfThisMonth(
      addMonth(targetStatement?.statementDate, addMonthAmount),
    );
    state?.activitySummaryList.push({
      label: formatAsMonthDayOnly(targetDate),
      content: message,
      active: isDateAfterTarget(getMoment(), targetDate),
    });
  }
};

const checkAndAddStatementGenerated = (
  c,
  state,
  forceNoActionRequired,
  addMonthAmount = 1,
) => {
  if (!state.isAutomaticPayment && !forceNoActionRequired) {
    let message = `Your secured account statement is ready `;
    const targetDate = setDate(
      addMonth(state?.recentStatement?.statementDate, addMonthAmount),
      1,
    );
    state?.activitySummaryList.push({
      label: formatAsMonthDayOnly(targetDate),
      content: message,
      active: isDateAfterTarget(state?.refTime, targetDate),
    });
  } else {
    let message = `Your secured account statement is ready - no action required`;
    const targetDate = setDate(
      addMonth(state?.recentStatement?.statementDate, addMonthAmount),
      1,
    );
    state?.activitySummaryList.push({
      label: formatAsMonthDayOnly(targetDate),
      content: message,
      active: isDateAfterTarget(state?.refTime, targetDate),
    });
  }
};

const checkAndAddBureausUpdated = (c, state, addMonthAmount) => {
  const targetDate = setDate(
    addMonth(state?.recentStatement?.statementDate, addMonthAmount),
    5,
  );
  state?.activitySummaryList.push({
    label: formatAsMonthDayOnly(targetDate),
    content:
      "We've updated the credit bureaus to help build your credit history",
    active: false,
  });
};

const buildPreUtilizationActivitySummary = (c, state) => {
  state.activitySummaryList = [];
  state?.activitySummaryList.push({
    label: 'STEP 1',
    content:
      'Use part of your Sesame Cash balance to begin building your credit',
    active: true,
  });
  state?.activitySummaryList.push({
    label: 'STEP 2',
    content:
      'Now use your Sesame Cash debit purchases to build your credit even more',
    active: true,
    showEmptyCircle: true,
    continue: true,
  });
  state?.activitySummaryList.push({
    label: 'STEP 3',
    content:
      'We’ll update the bureaus monthly and notify you as your score changes',
  });
};

const buildReducedActivitySummary = (c, state) => {
  state.activitySummaryList = [];

  let lookingForSesameCashPurchasesAdjustment = 0;

  if (state?.highLevelState === STATE___NO_BALANCE_INCURRED) {
    lookingForSesameCashPurchasesAdjustment = 0;
  }
  checkAndAddLookingForSesameCashPurchases(
    c,
    state,
    -1 + lookingForSesameCashPurchasesAdjustment,
  );
  const targetDate = getLastDayOfThisMonth(
    addMonth(state?.previousStatement?.statementDate, -1),
  );
  state?.activitySummaryList.push({
    label: formatAsMonthDayOnly(targetDate),
    content: ` We couldn’t find any debit purchases below $${state?.securedCardSettings?.maxUtilizationAmount} this month. Use Sesame Cash to build credit!`,
    active: true,
  });

  checkAndAddStatementGenerated(c, state, true, 0);
  checkAndAddBureausUpdated(c, state);
};

const buildDelinquentActivitySummary = (c, state) => {
  state.activitySummaryList = [];

  checkAndAddLookingForSesameCashPurchases(
    c,
    state,
    -1,
    state?.previousStatement?.statementDate,
  );
  checkAndAddAttemptToFindPurchases(c, state, -1);
  checkAndAddStatementGenerated(c, state, undefined, 0);

  if (!state.isAutomaticPayment) {
    let message = `You have an overdue balance - manually pay down your balance as soon as possible.`;
    if (state.paymentStatus === 'SUCEEDED') {
      message =
        'Your vault was used to pay off the balance on your secured account';
    }
    const targetDate = setDate(
      addMonth(state?.previousStatement?.statementDate, 0),
      25,
    );
    state?.activitySummaryList.push({
      label: formatAsMonthDayOnly(targetDate),
      content: message,
      active: state.paymentStatus === 'SUCEEDED',
    });
  } else {
    let message = `You have an overdue balance - manually pay down your balance as soon as possible.`;
    if (state.paymentStatus === 'SUCEEDED') {
      message = `Your vault was used to pay off the balance on your secured account`;
    }
    const targetDate = setDate(
      addMonth(state?.previousStatement?.statementDate, 0),
      2,
    );
    state?.activitySummaryList.push({
      label: formatAsMonthDayOnly(targetDate),
      content: message,
      active: state?.previousStatement?.paymentStatus === 'SUCEEDED',
    });
  }

  checkAndAddBureausUpdated(c, state, 1);
};

const buildStandardActivitySummary = (c, state) => {
  state.activitySummaryList = [];

  let monthAdjustment = 0;
  let findingPurchaseAdjustment = 0;
  let statementPaymentAdjustment = 0;
  let checkAndAddBureausUpdatedAdjustment = 0;
  let statementGeneratedAdjustment = 0;
  let targetStatement = state.previousStatement;
  if (state?.highLevelState === STATE___BALANCE_BUILDING) {
    targetStatement = state?.currentStatement;
  }
  checkAndAddLookingForSesameCashPurchases(
    c,
    state,
    undefined,
    targetStatement?.statementDate,
  );
  checkAndAddAttemptToFindPurchases(
    c,
    state,
    -1 + findingPurchaseAdjustment,
    targetStatement,
  );
  checkAndAddStatementGenerated(
    c,
    state,
    false,
    0 + monthAdjustment + statementGeneratedAdjustment,
  );

  if (!state.isAutomaticPayment) {
    let message = `Pay your credit balance with the money in your vault—${state.daysUntilPaymentDue} days left to approve`;
    if (state.paymentStatus === 'COMPLETE') {
      message =
        'Your vault was used to pay off the balance on your secured account';
    }
    const targetDate = setDate(
      addMonth(targetStatement?.statementDate, 0 + statementPaymentAdjustment),
      25,
    );
    state?.activitySummaryList.push({
      label: formatAsMonthDayOnly(targetDate),
      content: message,
      active:
        state?.previousStatement?.paymentStatus === 'SUCEEDED' ||
        state?.previousStatement?.paymentStatus === 'COMPLETED',
    });
  } else {
    let message = `We’ve automatically paid off the balance on your secured account`;
    const targetDate = setDate(
      addMonth(targetStatement?.statementDate, 0 + statementPaymentAdjustment),
      2,
    );
    if (state.paymentStatus === 'FAILED') {
      const daysLeftUntilPayment = state.daysUntilPaymentDue;
      message = `We weren't able to pay down your balance automatically. Manually pay down your balance within ${daysLeftUntilPayment} days.`;
      setDate(targetDate, 25);
    }

    state?.activitySummaryList.push({
      label: formatAsMonthDayOnly(targetDate),
      content: message,
      active:
        state?.previousStatement?.paymentStatus === 'SUCEEDED' ||
        state?.previousStatement?.paymentStatus === 'COMPLETED',
    });
  }

  checkAndAddBureausUpdated(c, state, 1 + checkAndAddBureausUpdatedAdjustment);
};

const buildActivitySummary = (c, state) => {
  if (state.highLevelState === STATE___NO_PAYMENT_MANAGEMENT) {
    buildPreUtilizationActivitySummary(c, state);
  } else if (state.highLevelState === STATE___NO_BALANCE_INCURRED) {
    buildReducedActivitySummary(c, state);
  } else if (state.highLevelState === STATE___DELIQUENT) {
    buildDelinquentActivitySummary(c, state);
  } else {
    buildStandardActivitySummary(c, state);
  }
};

/*
    inputs needed
      1. /secured-card/statements
      2. /card/details

 */
export const getBuilderState = c => {
  let state = {
    highLevelState: null,
    hideBanner: true,
  };
  state.refTime = c?.bankingConfig?.config?.serverTime;
  state.statements = c?.recentSecuredCardStatements;
  state.transactions = c?.recentBuilderTransaction;
  state.previousStatement =
    c?.recentSecuredCardStatements?.length > 1
      ? c?.recentSecuredCardStatements[1]
      : null;

  state.newBuilderUser = false;
  if (c?.recentSecuredCardStatements.length === 1) {
    state.currentStatement = c?.recentSecuredCardStatements[0];
    state.previousStatement = c?.recentSecuredCardStatements[0];
    state.newBuilderUser = true;
  } else {
    state.currentStatement =
      c?.recentSecuredCardStatements?.length > 0
        ? c?.recentSecuredCardStatements[0]
        : null;
  }

  state.hasSelectedUtilizationAndPaymentManagement =
    c?.bankingConfig?.uiFlags?.filter(x => x === 'securedCardUtilization')
      .length > 0;
  state.debitCard = c?.allCardDetails?.cards?.find(c => c?.type === 'CREDIT');
  state.creditPurse = c?.allCardDetails?.cards?.find(
    c => c?.type === 'CREDIT_PURSE',
  );
  state.physicalCard = c?.allCardDetails?.cards?.find(
    c => c?.type === 'PHYSICAL',
  );
  state.utilizationMethod = c?.securedCardSettings?.utilizationMethod;
  state.builderCreditLimit = state?.debitCard?.creditLimit;
  state.builderLastFourDigits = state?.debitCard?.lastFourDigits;
  state.builderBalance = state?.debitCard?.balance;
  state.totalCardBalance =
    c?.sesameCashAccount?.balance + state.builderCreditLimit;
  state.securedCardSettings = c?.securedCardSettings;
  state.vaultBalance = state?.creditPurse?.balance;
  state.remainingStatementBalance =
    state?.previousStatement?.remainingStatementBalance;
  state.spendableBalance = c?.sesameCashAccount?.balance;
  state.securedBuilderBalance = state?.debitCard?.creditLimit;
  state.utilizedBuilderBalance = state?.debitCard?.balance;
  state.reservedBuilderPayment = state?.vaultBalance;

  const utilFromCard = state?.securedCardSettings?.maxUtilizationAmount;
  state.maxUtilizationAmount =
    utilFromCard > 0 ? formatCurrency(utilFromCard) : '$0.00';
  state.maxUtilizationValue = utilFromCard;

  if (!state.debitCard) {
    return state;
  }

  state.creditUtilized = Math.abs(
    state?.previousStatement?.remainingStatementBalance,
  );

  const highLevelState = getHighLevelState(c, state);

  const isUtilizationSet = highLevelState !== STATE___NO_PAYMENT_MANAGEMENT;
  state.totalBuilderAccountBalance =
    c?.sesameCashAccount.balance +
    state.spendableBalance -
    state.builderBalance +
    state.vaultBalance;
  state.totalBuilderAccountBalance = !isUtilizationSet
    ? state.securedBuilderBalance + state.reservedBuilderPayment
    : state.securedBuilderBalance +
      state.reservedBuilderPayment +
      state.utilizedBuilderBalance;
  state.totalCashBalance =
    state?.totalBuilderAccountBalance + state.spendableBalance;

  state.totalCreditUtilized = Math.abs(
    state?.currentStatement?.statementBalance,
  );
  state.recentStatement =
    state?.highLevelState === STATE___BALANCE_BUILDING
      ? state?.currentStatement
      : state?.previousStatement;
  if (state.previousStatement) {
    state.daysUntilPaymentDue = getDiffDaysBetweenDates(
      state?.recentStatement?.paymentDueDate,
      getDateWithoutTime(state?.refTime),
    );
  }
  //state.highLevelState = STATE___ZERO_UTILIZATION;
  state.creditUtilizationPercent = Math.round(
    (state?.totalCreditUtilized / state?.debitCard?.creditLimit) * 100,
  );
  if (highLevelState) {
    const extraState = stateMapping[state.highLevelState](c, state);
    Object.assign(state, extraState);
  }

  buildActivitySummary(c, state);

  return state;
};
